import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { useCart } from "../../context/CartContext";
import { t } from "../../lib/i18n";
import { List, X, ShoppingBagOpen, Lock, CaretDown } from "@phosphor-icons/react";

const navItems = [
  { to: "/",          key: "nav.home" },
  { to: "/about",     key: "nav.about" },
  { to: "/services",  key: "nav.services" },
  { to: "/products",  key: "nav.products" },
  { to: "/portfolio", key: "nav.portfolio" },
  { to: "/blog",      key: "nav.blog" },
  { to: "/vlogs",     key: "nav.vlogs" },
  { to: "/booking",   key: "nav.booking" },
  { to: "/contact",   key: "nav.contact" },
];

// Authorized brand dealerships — extensible (add d&b, Sennheiser, etc. here later)
const BRANDS = [
  { to: "/brands/amate-audio", label: "Amate Audio", note: "Authorized Dealer · Cyprus" },
];

export default function Header() {
  const { lang, setLang } = useLang();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#08080a]/85 border-b border-white/5" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src="/mpsound-logo-neon.png" alt="MP Sound" className="h-16 sm:h-24 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${isActive ? "neon-text" : "text-gray-300 hover:text-white"}`
              }
              data-testid={`nav-${item.key.split(".")[1]}`}
            >
              {t(item.key, lang)}
            </NavLink>
          ))}
          {/* Brands dropdown — Authorized dealerships */}
          <div
            className="relative"
            onMouseEnter={() => setBrandsOpen(true)}
            onMouseLeave={() => setBrandsOpen(false)}
            data-testid="brands-dropdown"
          >
            <button
              type="button"
              className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-300 hover:text-white flex items-center gap-1"
              data-testid="brands-toggle"
              onClick={() => setBrandsOpen((v) => !v)}
            >
              {lang === "gr" ? "Brands" : "Brands"}
              <CaretDown size={12} weight="bold" className={`transition-transform ${brandsOpen ? "rotate-180" : ""}`} />
            </button>
            {brandsOpen && (
              <div
                className="absolute right-0 top-full pt-3 z-50"
                data-testid="brands-menu"
              >
                <div className="min-w-[260px] bg-[#0c0c10] border border-white/10 shadow-2xl">
                  {BRANDS.map((b) => (
                    <Link
                      key={b.to}
                      to={b.to}
                      onClick={() => setBrandsOpen(false)}
                      className="block px-5 py-4 hover:bg-white/5 border-b border-white/5 last:border-b-0"
                      data-testid={`brands-item-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="display-font text-sm font-bold uppercase tracking-wide text-white">{b.label}</div>
                      <div className="mono-font text-[10px] tracking-[0.18em] uppercase text-[#ccff00] mt-1">{b.note}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center mono-font text-xs border border-[#2a2a35]">
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-2 ${lang === "en" ? "bg-[#ccff00] text-black" : "text-gray-300 hover:text-white"}`}
              data-testid="lang-en"
            >EN</button>
            <button
              onClick={() => setLang("gr")}
              className={`px-3 py-2 ${lang === "gr" ? "bg-[#ccff00] text-black" : "text-gray-300 hover:text-white"}`}
              data-testid="lang-gr"
            >GR</button>
          </div>

          <Link to="/quote" className="relative" data-testid="cart-link">
            <ShoppingBagOpen size={26} weight="bold" className="text-gray-300 hover:text-[#ccff00]" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#ccff00] text-black text-[10px] font-bold mono-font px-1.5 py-0.5">
                {count}
              </span>
            )}
          </Link>

          <Link
            to="/admin/login"
            aria-label="Admin"
            data-testid="header-admin-link"
            className="text-gray-300 hover:text-[#ccff00] hidden sm:inline-flex"
            title="Admin"
          >
            <Lock size={24} weight="bold" />
          </Link>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0d0d10] border-t border-white/5 px-6 py-6 space-y-4" data-testid="mobile-nav">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="block text-sm tracking-[0.2em] uppercase font-semibold text-gray-200 hover:text-[#ccff00]"
              data-testid={`mobile-nav-${item.key.split(".")[1]}`}
            >
              {t(item.key, lang)}
            </Link>
          ))}
          {/* Mobile Brands */}
          <div className="pt-2 border-t border-white/5">
            <div className="mono-font text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-3">Brands</div>
            {BRANDS.map((b) => (
              <Link
                key={b.to}
                to={b.to}
                onClick={() => setOpen(false)}
                className="block py-2"
                data-testid={`mobile-brands-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="display-font text-sm font-bold uppercase text-white">{b.label}</div>
                <div className="mono-font text-[10px] tracking-[0.18em] uppercase text-[#ccff00] mt-0.5">{b.note}</div>
              </Link>
            ))}
          </div>
          <div className="flex items-center mono-font text-xs border border-[#2a2a35] w-fit mt-4">
            <button onClick={() => setLang("en")} className={`px-3 py-2 ${lang === "en" ? "bg-[#ccff00] text-black" : "text-gray-300"}`}>EN</button>
            <button onClick={() => setLang("gr")} className={`px-3 py-2 ${lang === "gr" ? "bg-[#ccff00] text-black" : "text-gray-300"}`}>GR</button>
          </div>
        </div>
      )}
    </header>
  );
}
